import { api } from "./api";

// ════════════════════════════════════════════════════════════════════════════
// Client API Types
// ════════════════════════════════════════════════════════════════════════════

export interface Client {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  birthday: string | null;
  preferredProviderId: string | null;
  preferredProviderName?: string | null;
  allergies: string | null;
  notes: string | null;
  tags: string[];
  totalSpend: string;
  lastVisitAt: string | null;
  appointmentCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClientAppointment {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  status: string;
  confirmationCode: string;
  serviceName: string;
  servicePrice: string;
  providerName: string;
  createdAt: string;
}

export interface ClientSignedForm {
  id: string;
  formName: string;
  signedAt: string;
}

export interface ClientStats {
  totalAppointments: number;
  completedAppointments: number;
  noShows: number;
  cancellations: number;
}

export interface ClientProfile extends Client {
  stripeCustomerId: string | null;
  recentAppointments: ClientAppointment[];
  signedForms: ClientSignedForm[];
  stats: ClientStats;
}

export interface ClientListResponse {
  clients: Client[];
  total: number;
  limit: number;
  offset: number;
}

// ════════════════════════════════════════════════════════════════════════════
// Client CRUD
// ════════════════════════════════════════════════════════════════════════════

export async function getClients(params?: {
  search?: string;
  tag?: string;
  limit?: number;
  offset?: number;
}): Promise<ClientListResponse> {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set("search", params.search);
  if (params?.tag) searchParams.set("tag", params.tag);
  if (params?.limit) searchParams.set("limit", params.limit.toString());
  if (params?.offset) searchParams.set("offset", params.offset.toString());

  const queryString = searchParams.toString();
  const url = queryString ? `/api/clients?${queryString}` : "/api/clients";

  return api<ClientListResponse>(url);
}

export async function getClient(id: string): Promise<ClientProfile> {
  const response = await api<{ client: ClientProfile }>(`/api/clients/${id}`);
  return response.client;
}

export async function createClient(data: {
  name: string;
  email?: string;
  phone?: string;
  birthday?: string;
  preferredProviderId?: string;
  allergies?: string;
  notes?: string;
  tags?: string[];
}): Promise<Client> {
  const response = await api<{ client: Client }>("/api/clients", {
    method: "POST",
    body: JSON.stringify(data),
  });
  return response.client;
}

export async function updateClient(
  id: string,
  data: {
    name?: string;
    email?: string | null;
    phone?: string | null;
    birthday?: string | null;
    preferredProviderId?: string | null;
    allergies?: string | null;
    notes?: string | null;
    tags?: string[];
  }
): Promise<Client> {
  const response = await api<{ client: Client }>(`/api/clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
  return response.client;
}

export async function deleteClient(id: string): Promise<void> {
  await api<{ success: boolean }>(`/api/clients/${id}`, {
    method: "DELETE",
  });
}

// ════════════════════════════════════════════════════════════════════════════
// Tags
// ════════════════════════════════════════════════════════════════════════════

export async function updateClientTags(
  id: string,
  tags: string[]
): Promise<{ id: string; tags: string[] }> {
  return api<{ id: string; tags: string[] }>(`/api/clients/${id}/tags`, {
    method: "PATCH",
    body: JSON.stringify({ tags }),
  });
}

export async function getAllTags(): Promise<string[]> {
  const response = await api<{ tags: string[] }>("/api/clients/tags/list");
  return response.tags;
}

// ════════════════════════════════════════════════════════════════════════════
// Notes
// ════════════════════════════════════════════════════════════════════════════

export async function addClientNote(
  id: string,
  note: string
): Promise<{ id: string; notes: string }> {
  return api<{ id: string; notes: string }>(`/api/clients/${id}/notes`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

